import { KeyRound, ShieldCheck } from "lucide-react";
import { useCallback, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../../api/client.js";
import { useMe, useStepUp } from "../../api/users.js";
import { ErrorMessage } from "../../ui/feedback.js";
import { Dialog } from "../../ui/overlay.js";
import { Button, TextField } from "../../ui/primitives.js";

/** Thrown by `guard` when the person closes the step-up dialog; callers ignore it. */
export class StepUpCancelled extends Error {
  constructor() {
    super("step-up cancelled");
    this.name = "StepUpCancelled";
  }
}

export const isStepUpRequired = (error: unknown): boolean =>
  error instanceof ApiProblem && error.problem?.reason === "step_up_required";

/**
 * Changes of roles and access need a fresh second-factor check (docs/users.md).
 * `guard(change)` runs the change; when the API asks for a step-up it opens
 * the dialog, and after a passed check runs the change once more. Render
 * `dialog` once on the page.
 */
export function useStepUpGuard(): {
  guard: <T>(change: () => Promise<T>) => Promise<T>;
  dialog: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const waiting = useRef<{ resolve: () => void; reject: (error: Error) => void } | null>(null);

  const guard = useCallback(async <T,>(change: () => Promise<T>): Promise<T> => {
    try {
      return await change();
    } catch (error) {
      if (!isStepUpRequired(error)) throw error;
      await new Promise<void>((resolve, reject) => {
        waiting.current = { resolve, reject };
        setOpen(true);
      });
      return change();
    }
  }, []);

  const finish = (passed: boolean) => {
    setOpen(false);
    if (passed) waiting.current?.resolve();
    else waiting.current?.reject(new StepUpCancelled());
    waiting.current = null;
  };

  return {
    guard,
    dialog: (
      <StepUpDialog open={open} onPassed={() => finish(true)} onCancel={() => finish(false)} />
    ),
  };
}

/** The check itself: a code of the authenticator app, or the person's passkey. */
export function StepUpDialog({
  open,
  onPassed,
  onCancel,
}: {
  open: boolean;
  onPassed: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [attempt, setAttempt] = useState(0);
  const close = (next: boolean) => {
    if (next) return;
    // A new form next time: no error of this attempt is left on it.
    setAttempt((n) => n + 1);
    onCancel();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("users.stepUp.title")}
      description={t("users.stepUp.lead")}
    >
      <StepUpForm key={attempt} onPassed={onPassed} />
    </Dialog>
  );
}

/**
 * The step-up's buttons and code field without a dialog around them: the
 * dialog above, and inline where a dialog does not fit (the assistant's
 * confirmation cards inside the chat).
 */
export function StepUpForm({ onPassed }: { onPassed: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const stepUp = useStepUp();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // The dialog sits in the form of the change it guards, and React sends a portal's
    // events up its component tree: unstopped, this submit submitted that change again.
    event.stopPropagation();
    const code = String(new FormData(event.currentTarget).get("code") ?? "").trim();
    stepUp.mutate({ code }, { onSuccess: onPassed });
  };
  return (
    <div className="flex flex-col gap-4">
      {me && me.passkeyCount > 0 && (
        <Button
          onClick={() => stepUp.mutate({ passkey: true }, { onSuccess: onPassed })}
          busy={stepUp.isPending}
          icon={<KeyRound className="size-4" aria-hidden />}
        >
          {t("users.stepUp.passkey")}
        </Button>
      )}
      {me?.totpEnabled && (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <TextField
            label={t("users.stepUp.code")}
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            minLength={6}
            maxLength={8}
          />
          <Button
            type="submit"
            variant={me.passkeyCount > 0 ? "secondary" : "primary"}
            busy={stepUp.isPending}
            icon={<ShieldCheck className="size-4" aria-hidden />}
          >
            {t("users.stepUp.confirm")}
          </Button>
        </form>
      )}
      <ErrorMessage error={stepUp.error} />
    </div>
  );
}
